
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CalendlyButtonProps {
  calendlyUrl: string;
  className?: string;
  personalInfo?: {
    fullName: string;
    email: string;
    company: string;
    designation: string;
  };
  assessmentTitle?: string;
  assessmentId?: string;
  pdfUrl?: string;
}

const CalendlyButton: React.FC<CalendlyButtonProps> = ({ 
  calendlyUrl, 
  className, 
  personalInfo,
  assessmentTitle,
  assessmentId,
  pdfUrl
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleRequestPremiumAccess = async () => {
    if (!notificationSent && personalInfo && assessmentTitle) {
      try {
        const { error } = await supabase.functions.invoke('send-premium-access-request', {
          body: {
            fullName: personalInfo.fullName,
            email: personalInfo.email,
            company: personalInfo.company,
            designation: personalInfo.designation,
            assessmentTitle: assessmentTitle,
            assessmentId: assessmentId,
            pdfUrl: pdfUrl || null
          }
        });

        if (error) {
          toast.error('Failed to send premium access request. Please try again.');
        } else {
          setNotificationSent(true);
          setShowSuccessDialog(true);
        }
      } catch (error) {
        console.error('Failed to send notification:', error);
        toast.error('Failed to send premium access request. Please try again.');
      }
    }
  };

  // Load Calendly script when the dialog is opened
  useEffect(() => {
    if (isOpen) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isOpen]);

  // Update the DB record with PDF URL when it becomes available after request was already sent
  useEffect(() => {
    if (notificationSent && pdfUrl && assessmentId && personalInfo?.email) {
      supabase.functions.invoke('send-premium-access-request', {
        body: {
          action: 'update-pdf-url',
          assessmentId: assessmentId,
          email: personalInfo.email,
          pdfUrl: pdfUrl
        }
      }).catch(err => console.error('Failed to update PDF URL on premium request:', err));
    }
  }, [notificationSent, pdfUrl, assessmentId, personalInfo?.email]);

  // Ensure the URL is valid
  const validCalendlyUrl = calendlyUrl?.startsWith('https://calendly.com/') 
    ? calendlyUrl 
    : 'https://calendly.com/gihan-gapstars'; // Default fallback
  return (
    <>
      <Button 
        onClick={handleRequestPremiumAccess}
        className={`bg-[#e87a2e] hover:bg-[#d16518] text-white ${className}`}
      >
        Request Premium Access
      </Button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl h-[80vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>Book a Call with Us</DialogTitle>
            <DialogDescription>
              Schedule a free consultation to discuss your assessment results and premium features.
            </DialogDescription>
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-4 top-4" 
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          
          <div className="h-full overflow-y-auto p-6 pt-0">
            <div 
              className="calendly-inline-widget"
              data-url={validCalendlyUrl}
              style={{ minWidth: '320px', height: '630px' }}
            ></div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => setShowSuccessDialog(false)}
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle>Thank You!</AlertDialogTitle>
            <AlertDialogDescription className="text-base pt-2">
              Thanks for your interest in the full version of the assessment! You're on our waitlist, and one of our team members will be in touch soon.
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CalendlyButton;
